import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  AlertCircle,
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock3,
  Coffee,
  Briefcase,
  LogIn,
  LogOut,
  Play,
  QrCode,
  TimerReset,
  UserRound,
  X,
  Camera,
  MapPin,
  Send,
  RefreshCw
} from "lucide-react";

import api from "../../api/axios";
import { useAuth } from "../../context/AuthContext";
import ReverseGeocode from "../../components/ReverseGeocode";
import {
  createIndiaDateTimeIso,
  formatIndiaTime,
  formatDurationSeconds,
  getAutoCheckoutDisplayTime,
  getBreakDurationSeconds,
  getIndiaDateKey,
  getIndiaTimeInputValue,
  getWorkDurationSeconds,
  isAutoCheckoutRecord,
  isCheckoutClosedBreak,
} from "../../utils/attendanceTime";
import { MIN_TEXT_WORDS, getMinimumWordsError } from "../../utils/textValidation";
import SelfieAttendanceModal from "../../components/attendance/SelfieAttendanceModal";
import QRAttendanceModal from "../../components/attendance/QRAttendanceModal";

const getAttendanceCacheKey = (employee) => {
  const id = employee?._id || employee?.id;
  return id ? `employee_attendance_summary_${id}` : null;
};

const ATTENDANCE_CACHE_TTL_MS = 30 * 1000;
// Keep a full calendar year in the employee history so the month accordions
// do not appear to lose older attendance after the most recent 30 days.
const ATTENDANCE_HISTORY_LIMIT = 365;
const TIME_12_HOURS = Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, "0"));
const TIME_MINUTES = Array.from({ length: 60 }, (_, index) => String(index).padStart(2, "0"));

const getAttendanceActionErrorMessage = (error, fallback = "Unable to update attendance. Please try again.") => {
  if (!error?.response || error.code === "ERR_NETWORK" || error.message === "Network Error") {
    return "Network connection problem. Please check your internet and try again.";
  }

  const status = error.response.status;
  const serverMessage = String(error.response?.data?.message || "").trim();
  const looksTechnical = /\b(referenceerror|typeerror|syntaxerror|circular structure|undefined|not defined|stack|\/controllers\/|\/utils\/)\b/i.test(serverMessage);
  // Geofence rejections are 403 responses. Keep their plain-language reason
  // (for example, GPS permission is off or the employee is outside the range)
  // instead of replacing it with a misleading generic permission error.
  if ([400, 403, 409, 422].includes(status) && serverMessage && serverMessage.length <= 180 && !looksTechnical) {
    return serverMessage;
  }
  if (status === 401) return "Your session has expired. Please sign in again.";
  if (status === 403) return "You do not have permission to perform this attendance action.";
  if (status === 408 || status === 504) return "The request took too long. Please try again.";
  if (status >= 500) return "Attendance could not be updated right now. Please try again later.";
  return fallback;
};

const readAttendanceCache = (employee) => {
  const cacheKey = getAttendanceCacheKey(employee);
  if (!cacheKey) return null;

  try {
    const cached = JSON.parse(localStorage.getItem(cacheKey) || "null");
    if (!cached?.data) return null;
    if (Date.now() - Number(cached.savedAt || 0) > ATTENDANCE_CACHE_TTL_MS) return null;
    return cached.data;
  } catch {
    localStorage.removeItem(cacheKey);
    return null;
  }
};

const readAnyAttendanceCache = () => {
  try {
    const keys = Object.keys(localStorage).filter((key) => key.startsWith("employee_attendance_summary_"));
    let latest = null;
    keys.forEach((key) => {
      const cached = JSON.parse(localStorage.getItem(key) || "null");
      if (!cached?.data) return;
      if (Date.now() - Number(cached.savedAt || 0) > ATTENDANCE_CACHE_TTL_MS) return;
      if (!latest || cached.savedAt > latest.savedAt) latest = cached;
    });
    return latest?.data || null;
  } catch {
    return null;
  }
};

const writeAttendanceCache = (employee, data) => {
  const cacheKey = getAttendanceCacheKey(employee);
  if (!cacheKey || !data) return;
  try {
    localStorage.setItem(cacheKey, JSON.stringify({ data, savedAt: Date.now() }));
  } catch {
    // Attendance still works if local storage is unavailable.
  }
};

const emptyAttendanceSummary = {
  attendanceStatus: { status: "not_checked_in" },
  weeklyData: {
    present: 0,
    totalHoursThisWeek: 0,
    totalSecondsThisWeek: 0,
    weekRange: {
      start: "Week",
      end: "Range",
    },
  },
  history: [],
};

const ATTENDANCE_LOCATION_CACHE_KEY = "employee_attendance_last_coords";
const SALES_LIVE_TRACKING_INTERVAL_MS = 60 * 1000;

const getTodayDateKey = () => {
  return getIndiaDateKey(new Date());
};

const toDateKey = (date) => {
  return getIndiaDateKey(date);
};

const getEmployeeJoiningDateKey = (employee = {}) => {
  const raw = String(employee?.attendanceStartDate || employee?.joiningDate || "").trim();
  if (!raw) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const dmy = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? "" : toDateKey(parsed);
};

const isWorkingDay = (date) => date.getDay() !== 0;

const getHolidayDateKeys = (holiday) => {
  const keys = new Set();
  if (holiday?.dateKey) keys.add(String(holiday.dateKey).slice(0, 10));
  if (!holiday?.date) return [...keys].filter(Boolean);

  const rawDate = String(holiday.date);
  const rawKey = rawDate.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(rawKey)) keys.add(rawKey);

  const parsedDate = new Date(holiday.date);
  if (!Number.isNaN(parsedDate.getTime())) keys.add(toDateKey(parsedDate));

  return [...keys].filter(Boolean);
};

const buildHistoryRowsWithMissingAbsences = (history = [], holidays = [], leaves = [], employee = {}, limit = ATTENDANCE_HISTORY_LIMIT) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const recordsByDate = new Map();
  history.forEach((record) => {
    const key = record?.date || (record?.checkIn ? toDateKey(record.checkIn) : "");
    if (key) recordsByDate.set(key, record);
  });

  const publicHolidaysByDate = new Map();
  holidays.forEach((holiday) => {
    getHolidayDateKeys(holiday).forEach((key) => {
      publicHolidaysByDate.set(key, holiday);
    });
  });
  const approvedLeavesByDate = new Map();
  const autoCompensatedLeavesByDate = new Map();

  const rows = [];
  const cursor = new Date(today);
  const joiningDateKey = getEmployeeJoiningDateKey(employee);

  while (rows.length < limit) {
    const dateKey = toDateKey(cursor);
    // Do not create attendance, weekly-off, or absent history before an
    // employee actually joined the company.
    if (joiningDateKey && dateKey < joiningDateKey) break;
    const existing = recordsByDate.get(dateKey);
    const holiday = publicHolidaysByDate.get(dateKey);
    const approvedLeave = approvedLeavesByDate.get(dateKey);
    const autoCompensatedLeave = autoCompensatedLeavesByDate.get(dateKey);

    if (holiday && cursor <= today) {
      rows.push({
        ...(existing || {}),
        _id: `holiday-${dateKey}`,
        date: dateKey,
        mode: "Holiday",
        status: "holiday",
        holidayName: holiday.name || "Holiday",
        isHolidayPlaceholder: true,
      });
    } else if (existing && !isAbsentRecord(existing)) {
      rows.push(existing);
    } else if (approvedLeave && cursor <= today) {
      rows.push(approvedLeave);
    } else if (autoCompensatedLeave && cursor <= today) {
      rows.push(autoCompensatedLeave);
    } else if (existing) {
      rows.push(existing);
    } else if (cursor < today && !isWorkingDay(cursor)) {
      rows.push({
        _id: `week-off-${dateKey}`,
        date: dateKey,
        mode: "Weekly Off",
        status: "week-off",
        isWeekOffPlaceholder: true,
      });
    } else if (cursor < today && isWorkingDay(cursor)) {
      rows.push({
        _id: `missing-absent-${dateKey}`,
        date: dateKey,
        mode: "",
        status: "absent",
        isMissingAbsent: true,
      });
    }

    cursor.setDate(cursor.getDate() - 1);
  }

  return rows;
};

const getHistoryMonthKey = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "unknown";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

const getHistoryMonthLabel = (monthKey) => {
  const [year, month] = String(monthKey || "").split("-").map(Number);
  if (!year || !month) return "Other records";
  return new Date(year, month - 1, 1).toLocaleDateString("en-IN", {
    month: "long",
    year: "numeric",
  });
};

const groupHistoryRowsByMonth = (rows = [], currentMonthKey = getHistoryMonthKey(new Date())) => {
  const groups = [];
  const byMonth = new Map();

  rows.forEach((row) => {
    const key = getHistoryMonthKey(row?.date || row?.checkIn);
    if (!byMonth.has(key)) {
      const group = {
        key,
        label: getHistoryMonthLabel(key),
        isCurrentMonth: key === currentMonthKey,
        rows: [],
      };
      byMonth.set(key, group);
      groups.push(group);
    }
    byMonth.get(key).rows.push(row);
  });

  return groups;
};

const readCachedCoords = () => {
  try {
    const cached = JSON.parse(localStorage.getItem(ATTENDANCE_LOCATION_CACHE_KEY) || "null");
    if (!cached?.coords || Date.now() - cached.savedAt > 10 * 60 * 1000) return null;
    return cached.coords;
  } catch {
    localStorage.removeItem(ATTENDANCE_LOCATION_CACHE_KEY);
    return null;
  }
};

const writeCachedCoords = (coords) => {
  if (!coords) return;
  try {
    localStorage.setItem(ATTENDANCE_LOCATION_CACHE_KEY, JSON.stringify({ coords, savedAt: Date.now() }));
  } catch {
    // Location cache only speeds up repeat attendance actions.
  }
};

const isSalesTeam = (team = "") => String(team || "").toLowerCase().includes("sales");

const formatDurationMinutes = (minutes = 0) => formatDurationSeconds(Number(minutes || 0) * 60);

const getLunchDurationMinutes = (record = {}) => {
  const storedMinutes = Number(record?.breakDuration || 0);
  if (storedMinutes > 0) return storedMinutes;

  if (Array.isArray(record?.breaks)) {
    const completedLunchMinutes = record.breaks.reduce((total, item) => {
      if (item?.type !== "lunch" || !item?.startTime || !item?.endTime) return total;
      const startMs = new Date(item.startTime).getTime();
      const endMs = new Date(item.endTime).getTime();
      if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return total;
      return total + ((endMs - startMs) / 60000);
    }, 0);
    if (completedLunchMinutes > 0) return completedLunchMinutes;
  }

  if (!record?.breakStart || !record?.breakEnd) return 0;
  const startMs = new Date(record.breakStart).getTime();
  const endMs = new Date(record.breakEnd).getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return 0;
  return (endMs - startMs) / 60000;
};

const isAbsentRecord = (record) => String(record?.status || "").toLowerCase() === "absent";
const isWeekOffRecord = (record) => String(record?.status || "").toLowerCase() === "week-off";
const isHolidayRecord = (record) => String(record?.status || "").toLowerCase() === "holiday";
const isApprovedLeaveRecord = (record) => (
  !record?.isCompensatedPresent &&
  ["leave", "comp-off"].includes(String(record?.status || "").toLowerCase())
);

const getTimeInputValue = (date) => {
  return getIndiaTimeInputValue(date);
};

const toTwelveHourParts = (value = "") => {
  if (!value) return { hour: "", minute: "", period: "AM" };
  const [hourValue = "", minuteValue = ""] = String(value || "").split(":");
  const hour24 = Number(hourValue);
  const minute = /^\d{2}$/.test(minuteValue) ? minuteValue : "00";
  const period = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 || 12;

  return {
    hour: Number.isNaN(hour24) ? "" : String(hour12).padStart(2, "0"),
    minute,
    period,
  };
};

const fromTwelveHourParts = ({ hour, minute, period }) => {
  const hourNumber = Number(hour);
  if (!hour || Number.isNaN(hourNumber)) return "";
  const hour24 = period === "PM" ? (hourNumber % 12) + 12 : hourNumber % 12;
  return `${String(hour24).padStart(2, "0")}:${minute || "00"}`;
};

const getAttendanceCorrectionHint = (record) => {
  if (!record?.checkIn || !record?.checkOut || isAbsentRecord(record) || isHolidayRecord(record)) return null;

  // The checkout column already identifies these records as "Auto checked out".
  // They do not require an additional correction warning on the employee view.
  if (isAutoCheckoutRecord(record)) return null;

  const checkOutMs = new Date(record.checkOut).getTime();
  const breakEndMs = record.breakEnd ? new Date(record.breakEnd).getTime() : NaN;
  const lunchWasClosedAtCheckout = isCheckoutClosedBreak(breakEndMs, checkOutMs);

  if (!lunchWasClosedAtCheckout) return null;

  return {
    title: "Lunch out missed",
    message: "",
    reason: "Lunch was not ended separately. Please verify and correct my attendance timing.",
    correctionAllowed: true,
  };
};

const Attendance = () => {
  const navigate = useNavigate();
  const { employee } = useAuth();
  const cachedSummary = useMemo(() => readAttendanceCache(employee) || readAnyAttendanceCache(), [employee]);

  const [attendanceStatus, setAttendanceStatus] = useState(
    cachedSummary?.attendanceStatus || emptyAttendanceSummary.attendanceStatus,
  );
  const [weeklyData, setWeeklyData] = useState(cachedSummary?.weeklyData || emptyAttendanceSummary.weeklyData);
  const [history, setHistory] = useState(cachedSummary?.history || emptyAttendanceSummary.history);
  const [earlyCheckoutOpen, setEarlyCheckoutOpen] = useState(false);
  const [earlyCheckoutReason, setEarlyCheckoutReason] = useState('');
  const [earlyCheckoutSaving, setEarlyCheckoutSaving] = useState(false);
  const [pendingSelfieCheckoutToken, setPendingSelfieCheckoutToken] = useState("");
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [correctionSaving, setCorrectionSaving] = useState(false);
  const [correctionForm, setCorrectionForm] = useState({
    date: new Date().toISOString().split("T")[0],
    checkIn: "",
    checkOut: "",
    requestedMode: "office",
    reason: "",
  });
  const [correctionRequests, setCorrectionRequests] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [expandedHistoryMonths, setExpandedHistoryMonths] = useState(() => new Set());
  const [locationError, setLocationError] = useState("");
  const [faceRegistered, setFaceRegistered] = useState(false);
  const [selfieAction, setSelfieAction] = useState("");
  const [qrModal, setQrModal] = useState({ isOpen: false, action: null, coords: null });
  const [loadError, setLoadError] = useState("");
  const [nowMs, setNowMs] = useState(Date.now());
  const actionQueueRef = useRef(Promise.resolve());
  const actionVersionRef = useRef(0);
  const pendingActionCountRef = useRef(0);
  const optimisticSummaryRef = useRef(null);
  const salesTrackingWatchRef = useRef(null);
  const salesTrackingLastPingRef = useRef(0);
  const salesTrackingSendingRef = useRef(false);
  const qrLocationPromiseRef = useRef(null);

  const fetchAll = useCallback(async () => {
    try {
      const res = await api.get("/api/employee/attendance/summary", {
        cache: false,
        params: { limit: ATTENDANCE_HISTORY_LIMIT, _t: Date.now() },
      });
      const serverData = res.data || {};
      setLoadError("");
      if (pendingActionCountRef.current > 0 && optimisticSummaryRef.current) {
        const mergedData = {
          ...serverData,
          attendanceStatus: optimisticSummaryRef.current.attendanceStatus,
          history: optimisticSummaryRef.current.history,
        };
        setAttendanceStatus(mergedData.attendanceStatus || {});
        setWeeklyData(mergedData.weeklyData || {});
        setHistory(mergedData.history || []);
        writeAttendanceCache(employee, mergedData);
        return;
      }

      optimisticSummaryRef.current = null;
      setAttendanceStatus(serverData.attendanceStatus || {});
      setWeeklyData(serverData.weeklyData || {});
      setHistory(serverData.history || []);
      writeAttendanceCache(employee, serverData);
    } catch (err) {
      console.error("Failed to load attendance summary:", err);
      setLoadError(err.response?.data?.message || "Failed to load latest attendance. Please refresh once.");
    }
  }, [employee]);

  useEffect(() => {
    api.get("/api/employee/attendance/face/status")
      .then(({ data }) => setFaceRegistered(Boolean(data?.registered)))
      .catch(() => setFaceRegistered(false));
  }, [employee?._id]);

  const fetchHolidays = useCallback(async () => {
    try {
      const res = await api.get("/api/holidays/public", {
        params: { _t: Date.now() },
      });
      setHolidays(Array.isArray(res.data) ? res.data : []);
    } catch {
      setHolidays([]);
    }
  }, []);

  const fetchLeaves = useCallback(async () => {
    try {
      const res = await api.get("/api/employee/leave/my", {
        params: { limit: 120, _t: Date.now() },
      });
      setLeaves(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load leave history for attendance:", err);
      setLeaves([]);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    fetchHolidays();
    fetchLeaves();
    const refresh = () => {
      if (!document.hidden) {
        fetchAll();
        fetchHolidays();
        fetchLeaves();
      }
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    const refreshInterval = window.setInterval(refresh, 15000);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
      window.clearInterval(refreshInterval);
    };
  }, [fetchAll, fetchHolidays, fetchLeaves]);

  useEffect(() => {
    if (!cachedSummary) return;
    setAttendanceStatus(cachedSummary.attendanceStatus || emptyAttendanceSummary.attendanceStatus);
    setWeeklyData(cachedSummary.weeklyData || emptyAttendanceSummary.weeklyData);
    setHistory(cachedSummary.history || emptyAttendanceSummary.history);
  }, [cachedSummary]);

  const fetchCorrections = useCallback(async () => {
    try {
      const res = await api.get("/api/employee/attendance-corrections");
      setCorrectionRequests(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load attendance corrections:", err);
    }
  }, []);

  useEffect(() => {
    fetchCorrections();
  }, [fetchCorrections]);

  const record = attendanceStatus?.record;

  useEffect(() => {
    const shouldTrack =
      isSalesTeam(employee?.team) &&
      record?.checkIn &&
      !record?.checkOut &&
      ["checked_in", "on_break"].includes(attendanceStatus?.status);

    if (!shouldTrack || !navigator.geolocation) {
      if (salesTrackingWatchRef.current !== null && navigator.geolocation) {
        navigator.geolocation.clearWatch(salesTrackingWatchRef.current);
        salesTrackingWatchRef.current = null;
      }
      return undefined;
    }

    const sendLiveLocation = async (position, force = false) => {
      const now = Date.now();
      if (!force && now - salesTrackingLastPingRef.current < SALES_LIVE_TRACKING_INTERVAL_MS) return;
      if (salesTrackingSendingRef.current) return;

      salesTrackingSendingRef.current = true;
      salesTrackingLastPingRef.current = now;

      const coords = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        capturedAt: new Date().toISOString(),
      };
      writeCachedCoords(coords);

      try {
        await api.post("/api/employee/attendance/live-location", { coords });
      } catch (err) {
        console.error("Sales live tracking ping failed:", err);
      } finally {
        salesTrackingSendingRef.current = false;
      }
    };

    navigator.geolocation.getCurrentPosition(
      (position) => sendLiveLocation(position, true),
      () => {},
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 30 * 1000 },
    );

    salesTrackingWatchRef.current = navigator.geolocation.watchPosition(
      (position) => sendLiveLocation(position),
      (error) => {
        console.error("Sales live tracking location failed:", error);
      },
      {
        enableHighAccuracy: true,
        maximumAge: 30 * 1000,
        timeout: 15000,
      },
    );

    return () => {
      if (salesTrackingWatchRef.current !== null) {
        navigator.geolocation.clearWatch(salesTrackingWatchRef.current);
        salesTrackingWatchRef.current = null;
      }
    };
  }, [attendanceStatus?.status, employee?.team, record?.checkIn, record?.checkOut]);

  useEffect(() => {
    const interval = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!navigator.geolocation || !navigator.permissions || readCachedCoords()) return;

    navigator.permissions.query({ name: "geolocation" }).then((permission) => {
      if (permission.state !== "granted") return;
      navigator.geolocation.getCurrentPosition(
        (position) => {
          writeCachedCoords({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        () => {},
        { enableHighAccuracy: false, timeout: 5000, maximumAge: 10 * 60 * 1000 },
      );
    }).catch(() => {});
  }, []);

  const getLocation = () => {
    const cachedCoords = readCachedCoords();
    if (cachedCoords) return Promise.resolve(cachedCoords);

    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation is not supported by your browser"));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          };
          writeCachedCoords(coords);
          resolve(coords);
        },
        (error) => {
          reject(new Error(error.code === 1 ? "Please enable location access to continue" : "Unable to retrieve your location"));
        },
        { enableHighAccuracy: false, timeout: 3500, maximumAge: 10 * 60 * 1000 },
      );
    });
  };



  const applyAttendanceRecord = (status, nextRecord) => {
    const activeBreak = nextRecord?.breaks?.find((item) => !item.endTime);
    const nextStatus = {
      status,
      record: nextRecord,
      ...(activeBreak ? { activeBreakType: activeBreak.type || "lunch" } : {}),
    };
    setAttendanceStatus({
      ...nextStatus,
    });
    setHistory((current) => {
      const withoutCurrent = current.filter((item) => item._id !== nextRecord?._id && item.date !== nextRecord?.date);
      const nextHistory = nextRecord ? [nextRecord, ...withoutCurrent].slice(0, 8) : current;
      const nextSummary = {
        attendanceStatus: nextStatus,
        weeklyData,
        history: nextHistory,
      };
      optimisticSummaryRef.current = nextSummary;
      writeAttendanceCache(employee, nextSummary);
      return nextHistory;
    });
  };

  const runAttendanceAction = (action, onSuccess, failureMessage, onOptimistic, options = {}) => {
    const actionVersion = actionVersionRef.current + 1;
    actionVersionRef.current = actionVersion;
    const previousStatus = attendanceStatus;
    const previousHistory = history;
    const optimisticRecord = onOptimistic?.();
    if (optimisticRecord) {
      applyAttendanceRecord(optimisticRecord.status, optimisticRecord.record);
    }
    options.afterOptimistic?.();

    setLocationError("");

    const runServerAction = async () => {
      pendingActionCountRef.current += 1;
      const attendanceToastId = toast.loading(
        options.toastMessage || (options.skipLocation || employee?.isWFH ? "Updating attendance…" : "Checking office location…"),
      );
      try {
        const coords = options.skipLocation || employee?.isWFH ? null : (options.preFetchedCoords || await getLocation());
        const res = await action(coords);
        if (actionVersion === actionVersionRef.current) {
          toast.dismiss(attendanceToastId);
          onSuccess?.(res.data);
          optimisticSummaryRef.current = null;
          window.setTimeout(fetchAll, 250);
        } else {
          toast.dismiss(attendanceToastId);
        }
      } catch (err) {
        if (actionVersion === actionVersionRef.current && optimisticRecord && !options.keepOptimisticOnError) {
          optimisticSummaryRef.current = null;
          setAttendanceStatus(previousStatus);
          setHistory(previousHistory);
        }
        if (actionVersion === actionVersionRef.current) {
          toast.error(getAttendanceActionErrorMessage(err, failureMessage), { id: attendanceToastId });
          options.onError?.(err);
          window.setTimeout(fetchAll, 250);
        } else {
          toast.dismiss(attendanceToastId);
        }
      } finally {
        pendingActionCountRef.current = Math.max(0, pendingActionCountRef.current - 1);
        options.onFinally?.();
      }
    };

    actionQueueRef.current = actionQueueRef.current
      .catch(() => {})
      .then(runServerAction);
  };

  const initiateQRScan = (actionType) => {
    if (employee?.isWFH || employee?.geofenceEnabled === false) {
       if (actionType === 'checkin') handleCheckIn();
       if (actionType === 'checkout') performCheckOut();
       if (actionType === 'break-start') handleBreakStart('lunch');
       if (actionType === 'break-end') handleBreakEnd();
       return;
    }

    const cachedCoords = readCachedCoords();
    // 1. OPEN QR SCAN MODAL INSTANTLY (0ms delay)
    setQrModal({ isOpen: true, action: actionType, coords: cachedCoords || null });

    // 2. PARALLEL BACKGROUND LOCATION DETECTION
    qrLocationPromiseRef.current = getLocation()
      .then((coords) => {
        setQrModal((prev) => (prev.isOpen ? { ...prev, coords } : prev));
        return coords;
      })
      .catch((err) => {
        console.warn("Background location detection:", err?.message || err);
        return null;
      });
  };

  const handleQRScanSuccess = async (qrToken) => {
     const { action, coords } = qrModal;
     setQrModal({ isOpen: false, action: null, coords: null });

     let finalCoords = coords || readCachedCoords();
     if (!finalCoords && qrLocationPromiseRef.current) {
        try {
           finalCoords = await qrLocationPromiseRef.current;
        } catch (err) {
           console.warn("Location error on QR scan:", err);
        }
     }

     if (action === 'checkin') handleCheckIn(qrToken, "", finalCoords);
     if (action === 'checkout') performCheckOut("", qrToken, "", finalCoords);
     if (action === 'break-start') handleBreakStart('lunch', qrToken, "", finalCoords);
     if (action === 'break-end') handleBreakEnd(qrToken, "", finalCoords);
  };

  const handleCheckIn = (qrToken = "", selfieToken = "", preFetchedCoords = null, selfiePhoto = "") => {
    const now = new Date().toISOString();
    const optimisticRecord = {
      _id: `temp-attendance-${Date.now()}`,
      employee: employee?._id,
      date: getTodayDateKey(),
      checkIn: now,
      checkOut: null,
      status: "present",
      mode: employee?.isWFH ? "wfh" : "office",
      breaks: [],
      breakDuration: 0,
      duration: 0,
    };

    const actionOptions = selfieToken && !preFetchedCoords ? { skipLocation: true } : (preFetchedCoords ? { preFetchedCoords } : {});

    runAttendanceAction(
      (coords) => api.post("/api/employee/attendance/checkin", { coords: preFetchedCoords || coords, qrToken, selfieToken, selfiePhoto, isGpsOnly: (!qrToken && !selfieToken) }),
      (record) => {
        if (!record?.checkIn) {
          setLocationError("Check-in was not saved. Please refresh and try again.");
          return;
        }
        applyAttendanceRecord("checked_in", record);
        if (selfieToken) toast.success("Selfie attendance marked successfully");
      },
      "Check-in failed",
      () => ({ status: "checked_in", record: optimisticRecord }),
      actionOptions,
    );
  };

  const performCheckOut = (earlyReason = "", qrToken = "", selfieToken = "", preFetchedCoords = null) => {
    const now = new Date().toISOString();
    const optimisticRecord = {
      ...(record || {}),
      checkOut: now,
      status: "present",
      breaks: (record?.breaks || []).map((item) => item.endTime ? item : { ...item, endTime: now }),
    };

    setEarlyCheckoutSaving(Boolean(earlyReason));
    
    const actionOptions = selfieToken ? { skipLocation: true } : (preFetchedCoords ? { preFetchedCoords } : {});
    actionOptions.onError = (error) => {
      setEarlyCheckoutSaving(false);
      if (error.response?.data?.code === "EARLY_REASON_REQUIRED") {
        setPendingSelfieCheckoutToken(selfieToken);
        setEarlyCheckoutOpen(true);
      }
    };

    runAttendanceAction(
      (coords) => api.post("/api/employee/attendance/checkout", { coords, qrToken, finalCheckout: true, earlyReason, selfieToken, isGpsOnly: (!qrToken && !selfieToken) }),
      (record) => {
        setEarlyCheckoutOpen(false);
        setEarlyCheckoutReason("");
        setPendingSelfieCheckoutToken("");
        setEarlyCheckoutSaving(false);
        applyAttendanceRecord("checked_out", record);
        if (selfieToken) toast.success("Selfie attendance marked successfully");
        navigate("/eod");
      },
      "Check-out failed",
      () => ({ status: "checked_out", record: optimisticRecord }),
      actionOptions,
    );
  };

  const handleCheckOut = () => {
    performCheckOut();
  };

  const submitEarlyCheckout = (event) => {
    event.preventDefault();
    const reasonError = getMinimumWordsError(earlyCheckoutReason, "Early checkout reason");
    if (reasonError) {
      setLocationError(reasonError);
      return;
    }
    setLocationError("");
    performCheckOut(earlyCheckoutReason.trim(), pendingSelfieCheckoutToken);
  };

  const handleBreakStart = (type = "lunch", qrToken = "", selfieToken = "", preFetchedCoords = null, selfiePhoto = "") => {
    const now = new Date().toISOString();
    const optimisticRecord = {
      ...(record || {}),
      breaks: [...(record?.breaks || []), { startTime: now, type }],
      breakStart: type === "lunch" ? (record?.breakStart || now) : record?.breakStart,
    };

    const actionOptions = selfieToken ? { skipLocation: true } : (preFetchedCoords ? { preFetchedCoords } : {});
    runAttendanceAction(
      (coords) => api.post("/api/employee/attendance/break/start", { type, coords, qrToken, selfieToken, selfiePhoto, isGpsOnly: (!qrToken && !selfieToken) }),
      (record) => {
        applyAttendanceRecord("on_break", {
          ...record,
          breakStart: record.breakStart || record.breaks?.find((item) => !item.endTime)?.startTime,
        });
        if (selfieToken) toast.success("Selfie attendance marked successfully");
      },
      "Action failed",
      () => ({ status: "on_break", record: optimisticRecord }),
      actionOptions,
    );
  };

  const handleBreakEnd = (qrToken = "", selfieToken = "", preFetchedCoords = null, selfiePhoto = "") => {
    const now = new Date().toISOString();
    const optimisticBreaks = (record?.breaks || []).map((item) => {
      return item.endTime ? item : { ...item, endTime: now };
    });
    const lunchSeconds = getBreakDurationSeconds({ ...(record || {}), breaks: optimisticBreaks }, new Date(now).getTime());
    const optimisticRecord = {
      ...(record || {}),
      breaks: optimisticBreaks,
      breakEnd: now,
      breakDuration: lunchSeconds / 60,
    };

    const actionOptions = selfieToken ? { skipLocation: true } : (preFetchedCoords ? { preFetchedCoords } : {});
    runAttendanceAction(
      (coords) => api.post("/api/employee/attendance/break/end", { coords, qrToken, selfieToken, selfiePhoto, isGpsOnly: (!qrToken && !selfieToken) }),
      (record) => {
        applyAttendanceRecord("checked_in", record);
        if (selfieToken) toast.success("Selfie attendance marked successfully");
      },
      "Action failed",
      () => ({ status: "checked_in", record: optimisticRecord }),
      actionOptions,
    );
  };

  const currentSelfieAction = attendanceStatus?.status === "not_checked_in"
    ? "checkin"
    : attendanceStatus?.status === "on_break"
      ? "break-end"
      : "break-start";

  const handleGpsAction = async (action) => {
    let coords = null;
    try {
      coords = await getLocation();
    } catch (e) {
      setLocationError("Device location is required for GPS attendance. Please enable it in your browser.");
      return;
    }
    if (action === "checkin") handleCheckIn("", "", coords);
    if (action === "break-start") handleBreakStart("lunch", "", "", coords);
    if (action === "break-end") handleBreakEnd("", "", coords);
    if (action === "checkout") performCheckOut("", "", "", coords);
  };

  const completeSelfieAttendance = async (selfieToken, photoData = "") => {
    const action = selfieAction;
    setSelfieAction("");
    let coords = null;
    try {
      coords = await getLocation();
    } catch {
      // Coords might not be available if GPS is disabled
    }
    if (action === "checkin") handleCheckIn("", selfieToken, coords, photoData);
    if (action === "break-start") handleBreakStart("lunch", "", selfieToken, coords, photoData);
    if (action === "break-end") handleBreakEnd("", selfieToken, coords, photoData);
    if (action === "checkout") performCheckOut("", "", selfieToken, coords);
  };

  const combineDateTime = (date, time) => {
    if (!date || !time) return "";
    const [hour, minute] = String(time).split(":").map(Number);
    return createIndiaDateTimeIso(date, hour, minute, 0, 0) || "";
  };

  const submitCorrection = async (event) => {
    event.preventDefault();
    if (!correctionForm.date || !correctionForm.reason.trim()) {
      setLocationError("Date and reason are required for correction requests");
      return;
    }

    const reasonError = getMinimumWordsError(correctionForm.reason, "Correction reason");
    if (reasonError) {
      setLocationError(reasonError);
      return;
    }

    setCorrectionSaving(true);
    setLocationError("");
    try {
      await api.post("/api/employee/attendance-corrections", {
        date: correctionForm.date,
        requestedCheckIn: combineDateTime(correctionForm.date, correctionForm.checkIn),
        requestedCheckOut: combineDateTime(correctionForm.date, correctionForm.checkOut),
        requestedMode: correctionForm.requestedMode,
        reason: correctionForm.reason.trim(),
      });
      setCorrectionOpen(false);
      setCorrectionForm({
        date: new Date().toISOString().split("T")[0],
        checkIn: "",
        checkOut: "",
        requestedMode: "office",
        reason: "",
      });
      fetchCorrections();
    } catch (err) {
      setLocationError(err.response?.data?.message || "Failed to submit correction request");
    } finally {
      setCorrectionSaving(false);
    }
  };

  const openCorrectionForRecord = (item, reason = "") => {
    setCorrectionForm({
      date: String(item?.date || "").slice(0, 10) || toDateKey(item?.checkIn || new Date()),
      checkIn: getTimeInputValue(item?.checkIn),
      checkOut: getTimeInputValue(item?.checkOut),
      requestedMode: item?.mode || "office",
      reason,
    });
    setCorrectionOpen(true);
  };

  const formatTime = (time) => {
    return formatIndiaTime(time, "-");
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
    });
  };

  const formatHistoryTime = (item, time) => {
    if (isHolidayRecord(item) || isApprovedLeaveRecord(item) || isWeekOffRecord(item)) return "-";
    if (isAbsentRecord(item)) return "Absent";
    return formatTime(time);
  };

  const AutoCheckoutDisplay = ({ time }) => (
    <span className="inline-flex max-w-full flex-wrap items-center gap-1.5">
      <span className="whitespace-nowrap">{time}</span>
      <span className="whitespace-nowrap rounded-full bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-black uppercase leading-none text-amber-700 ring-1 ring-amber-500/20 dark:text-amber-300">
        Auto checked out
      </span>
    </span>
  );

  const formatHistoryCheckOut = (item) => {
    if (isAutoCheckoutRecord(item)) return <AutoCheckoutDisplay time={getAutoCheckoutDisplayTime(item, formatTime)} />;
    return formatHistoryTime(item, item.checkOut);
  };

  const formatCheckout = (item) => (
    isAutoCheckoutRecord(item) ? <AutoCheckoutDisplay time={getAutoCheckoutDisplayTime(item, formatTime)} /> : formatTime(item?.checkOut)
  );

  const formatHistoryHours = (item) => {
    if (isHolidayRecord(item) || isApprovedLeaveRecord(item) || isWeekOffRecord(item)) return "-";
    if (isAbsentRecord(item)) return "Absent";
    return item.checkIn ? formatDurationSeconds(getWorkDurationSeconds(item, nowMs)) : "-";
  };

  const formatHistoryStatus = (status) => {
    const normalized = String(status || "present").toLowerCase();
    if (normalized === "comp-off") return "Comp Off";
    if (normalized === "loss-of-pay") return "Loss of Pay";
    if (normalized === "week-off") return "Weekly Off";
    if (normalized === "leave") return "Leave";
    return normalized.replace("-", " ");
  };

  const attendanceView = getAttendanceView(attendanceStatus, record, formatTime, formatCheckout);
  const weekSeconds = Number(
    weeklyData?.totalSecondsThisWeek ?? Number(weeklyData?.totalHoursThisWeek || 0) * 3600,
  );
  const weekProgress = Math.min(100, Math.round((weekSeconds / (40 * 60 * 60)) * 100));
  const completedDays = weeklyData?.present || 0;
  const avgSeconds = completedDays > 0 ? Math.floor(weekSeconds / completedDays) : 0;
  const liveBreakSeconds = record?.breakStart ? Math.floor((nowMs - new Date(record.breakStart).getTime()) / 1000) : 0;
  const workDuration = formatDurationSeconds(getWorkDurationSeconds(record, nowMs));
  const lunchDuration = formatDurationMinutes(getLunchDurationMinutes(record));
  const todayLabel = new Date().toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const historyRows = useMemo(
    () => buildHistoryRowsWithMissingAbsences(history, holidays, leaves, employee),
    [history, holidays, leaves, employee],
  );
  const currentHistoryMonthKey = useMemo(() => getHistoryMonthKey(new Date()), []);
  const historyMonthGroups = useMemo(
    () => groupHistoryRowsByMonth(historyRows, currentHistoryMonthKey),
    [historyRows, currentHistoryMonthKey],
  );
  const toggleHistoryMonth = useCallback((monthKey) => {
    setExpandedHistoryMonths((current) => {
      const next = new Set(current);
      if (next.has(monthKey)) {
        next.delete(monthKey);
      } else {
        next.add(monthKey);
      }
      return next;
    });
  }, []);
  const recentCorrections = useMemo(() => correctionRequests.slice(0, 8), [correctionRequests]);

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]">
      <main className="attendance-premium">
        <section className="attendance-shell">
          <div className="attendance-hero">
            <div>
              <span className="premium-eyebrow">Attendance Control</span>
              <h1>Workday tracker</h1>
              <div className="premium-meta-row">
                <span>{new Date().toLocaleDateString("en-IN", { weekday: "long" })}</span>
                <span>{todayLabel}</span>
              </div>
            </div>

          </div>
          {loadError && (
            <div className="attendance-alert">
              <AlertCircle size={18} />
              <span>{loadError}</span>
            </div>
          )}

          {locationError && (
            <div className="attendance-alert">
              <AlertCircle size={18} />
              <span>{locationError}</span>
            </div>
          )}
          <section className="mt-4 mb-4 flex flex-col gap-4">

              {/* QR Attendance Card */}
              <div className="relative rounded-2xl border border-gray-100 bg-white p-5 flex flex-col sm:flex-row items-center gap-5 shadow-[0_2px_16px_rgba(0,0,0,0.04)] overflow-hidden">
                <div className="absolute left-0 top-0 bottom-0 w-[5px] bg-emerald-500 rounded-l-2xl"></div>
                <div className="w-[72px] h-[72px] shrink-0 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-600 ml-3">
                  <QrCode size={34} strokeWidth={1.8} />
                </div>
                <div className="flex-1 py-1 text-left">
                  <h2 className="text-[17px] font-bold text-gray-900 mb-1">QR attendance</h2>
                  <p className="text-[13px] text-gray-500 mb-2.5">Mark attendance using device location & office QR code.</p>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-100 px-3 py-1 text-[11.5px] font-semibold text-emerald-700">
                    <CheckCircle2 size={13} strokeWidth={2.5} className="text-emerald-500" />
                    Office GPS & QR required
                  </span>
                </div>
                <div className="flex flex-col shrink-0 items-end justify-center gap-2 pr-2 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={() => initiateQRScan(currentSelfieAction)}
                    className="inline-flex items-center gap-2.5 rounded-full bg-emerald-600 pl-4 pr-3 py-2.5 text-[13.5px] font-bold text-white shadow-md shadow-emerald-100 hover:bg-emerald-700 transition-colors"
                  >
                    <QrCode size={16} strokeWidth={2} />
                    {currentSelfieAction === "checkin" ? "QR check in" : currentSelfieAction === "break-end" ? "QR lunch out" : "QR lunch in"}
                    <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                      <ChevronRight size={14} strokeWidth={2.5} />
                    </span>
                  </button>
                  {attendanceStatus?.status === "checked_in" && (
                    <button type="button" onClick={() => initiateQRScan("checkout")} className="text-[13px] font-semibold text-red-500 hover:text-red-700 transition-colors">
                      QR check out
                    </button>
                  )}
                </div>
              </div>

              {/* GPS Attendance Card */}
              <div className="relative rounded-2xl border border-gray-100 bg-white p-5 flex flex-col sm:flex-row items-center gap-5 shadow-[0_2px_16px_rgba(0,0,0,0.04)] overflow-hidden">
                <div className="absolute left-0 top-0 bottom-0 w-[5px] bg-[#4361EE] rounded-l-2xl"></div>
                <div className="w-[72px] h-[72px] shrink-0 rounded-2xl bg-blue-50 flex items-center justify-center text-[#4361EE] ml-3">
                  <MapPin size={34} strokeWidth={1.8} />
                </div>
                <div className="flex-1 py-1 text-left">
                  <h2 className="text-[17px] font-bold text-gray-900 mb-1">GPS attendance</h2>
                  <p className="text-[13px] text-gray-500 mb-2.5">Mark attendance using only your location. No QR or Selfie required.</p>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 border border-blue-100 px-3 py-1 text-[11.5px] font-semibold text-[#4361EE]">
                    <CheckCircle2 size={13} strokeWidth={2.5} className="text-[#4361EE]" />
                    GPS required
                  </span>
                </div>
                <div className="flex flex-col shrink-0 items-end justify-center gap-2 pr-2 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={() => handleGpsAction(currentSelfieAction)}
                    className="inline-flex items-center gap-2.5 rounded-full bg-[#4361EE] pl-4 pr-3 py-2.5 text-[13.5px] font-bold text-white shadow-md shadow-blue-100 hover:bg-blue-700 transition-colors"
                  >
                    <MapPin size={16} strokeWidth={2} />
                    {currentSelfieAction === "checkin" ? "GPS check in" : currentSelfieAction === "break-end" ? "GPS lunch out" : "GPS lunch in"}
                    <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                      <ChevronRight size={14} strokeWidth={2.5} />
                    </span>
                  </button>
                  {attendanceStatus?.status === "checked_in" && (
                    <button type="button" onClick={() => handleGpsAction("checkout")} className="text-[13px] font-semibold text-red-500 hover:text-red-700 transition-colors">
                      GPS check out
                    </button>
                  )}
                </div>
              </div>

              {/* Selfie Attendance Card */}
              {employee?.selfieEnabled !== false && (
                <div className="relative rounded-2xl border border-gray-100 bg-white p-5 flex flex-col sm:flex-row items-center gap-5 shadow-[0_2px_16px_rgba(0,0,0,0.04)] overflow-hidden">
                  <div className="absolute left-0 top-0 bottom-0 w-[5px] bg-[#7848AB] rounded-l-2xl"></div>
                  <div className="w-[72px] h-[72px] shrink-0 rounded-2xl bg-purple-50 flex items-center justify-center text-[#7848AB] ml-3">
                    <Camera size={34} strokeWidth={1.8} />
                  </div>
                  <div className="flex-1 py-1 text-left">
                    <h2 className="text-[17px] font-bold text-gray-900 mb-1">Selfie attendance</h2>
                    <p className="text-[13px] text-gray-500 mb-2.5">Use this if office GPS is unavailable or you are outside the office geofence. A live face match is required; location is not checked.</p>
                    <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11.5px] font-semibold ${faceRegistered ? "bg-purple-50 border-purple-100 text-[#7848AB]" : "bg-amber-50 border-amber-100 text-amber-700"}`}>
                      <CheckCircle2 size={13} strokeWidth={2.5} className={faceRegistered ? "text-[#7848AB]" : "text-amber-600"} />
                      {faceRegistered ? "Face registered" : "Registration required"}
                    </span>
                  </div>
                  <div className="flex flex-col shrink-0 items-end justify-center gap-2 pr-2 w-full sm:w-auto">
                    <button
                      type="button"
                      onClick={() => setSelfieAction(faceRegistered ? currentSelfieAction : "register")}
                      className="inline-flex items-center gap-2.5 rounded-full bg-[#7848AB] pl-4 pr-3 py-2.5 text-[13.5px] font-bold text-white shadow-md shadow-purple-100 hover:bg-purple-800 transition-colors"
                    >
                      <Camera size={16} strokeWidth={2} />
                      {faceRegistered ? (currentSelfieAction === "checkin" ? "Selfie check in" : currentSelfieAction === "break-end" ? "Selfie lunch out" : "Selfie lunch in") : "Register face"}
                      <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                        <ChevronRight size={14} strokeWidth={2.5} />
                      </span>
                    </button>
                    {faceRegistered && attendanceStatus?.status === "checked_in" && (
                      <button type="button" onClick={() => setSelfieAction("checkout")} className="text-[13px] font-semibold text-red-500 hover:text-red-700 transition-colors">
                        Selfie check out
                      </button>
                    )}
                  </div>
                </div>
              )}
            </section>

          <div className="attendance-grid">
            <section className="attendance-card attendance-status-card attendance-live-card">
              <div className="premium-card-header attendance-live-header">
                <div>
                  <span className="premium-section-kicker attendance-live-kicker">Live status</span>
                  <h2>Today</h2>
                </div>
                <span className={`premium-status-pill ${attendanceView.tone}`}>
                  <Clock3 size={16} />
                  {attendanceView.label}
                </span>
              </div>

              <div className="attendance-focus">
                <div className={`premium-attendance-icon ${attendanceView.tone}`}>
                  {attendanceView.icon}
                </div>
                <div>
                  <p className="premium-attendance-title">{attendanceView.title}</p>
                  <p className="premium-attendance-subtitle">{attendanceView.subtitle}</p>
                </div>
              </div>

              {attendanceStatus?.status !== "checked_out" && (
                <div className={`attendance-primary-orbit ${attendanceStatus?.status === "checked_in" ? "multi" : ""}`}>
                  {attendanceStatus?.status === "not_checked_in" && (
                    <AttendanceActionCircle
                      label="Check in"
                      icon={<Play size={34} fill="currentColor" />}
                      onClick={() => initiateQRScan('checkin')}
                    />
                  )}

                  {attendanceStatus?.status === "checked_in" && (
                    <>
                      <AttendanceActionCircle
                        label="Lunch in"
                        icon={<Coffee size={34} />}
                        onClick={() => initiateQRScan('break-start')}
                      />
                      <AttendanceActionCircle
                        label="Check out"
                        tone="danger"
                        icon={<LogOut size={34} />}
                        onClick={() => initiateQRScan('checkout')}
                      />
                    </>
                  )}

                  {attendanceStatus?.status === "on_break" && (
                    <AttendanceActionCircle
                      label={attendanceStatus?.activeBreakType === "lunch" ? "Lunch out" : "End break"}
                      icon={<Coffee size={34} />}
                      onClick={() => initiateQRScan('break-end')}
                    />
                  )}
                </div>
              )}

              {attendanceStatus?.status === "on_break" && (
                <div className="break-live-panel">
                  <span>{attendanceStatus?.activeBreakType?.includes("tea") ? "Tea break running" : "Lunch running"}</span>
                  <strong>{formatDurationSeconds(liveBreakSeconds)}</strong>
                </div>
              )}

              <div className="attendance-timeline-grid">
                <TimelineItem label="Check In" value={formatTime(record?.checkIn)} icon={<LogIn size={24} />} />
                <TimelineItem label="Lunch" value={lunchDuration} icon={<Coffee size={24} />} />
                <TimelineItem label="Check Out" value={formatCheckout(record)} icon={<LogOut size={24} />} />
                <TimelineItem label="Work Time" value={workDuration} icon={<Briefcase size={24} />} />
              </div>

              <div className="attendance-action-row flex-col">
                  <div className="mt-6 w-full">
                  </div>

              </div>
            </section>

            <aside className="attendance-side-stack">
                            <div className="bg-white rounded-[20px] border border-gray-100 p-5 shadow-[0_4px_24px_rgba(0,0,0,0.02)] flex flex-col gap-4">
                <div className="flex justify-between items-start">
                  <div>
                    <span className="text-[10px] font-bold text-purple-700 tracking-[0.1em] uppercase block mb-1">Correction</span>
                    <h2 className="text-[20px] font-bold text-gray-900 tracking-tight leading-tight">Fix attendance</h2>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-700 flex items-center justify-center shrink-0">
                    <TimerReset size={20} strokeWidth={2} />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setCorrectionOpen(true)}
                  className="w-full bg-[#8A2BE2] hover:bg-purple-700 text-white font-bold py-3.5 px-4 rounded-xl flex items-center justify-between transition-colors shadow-sm shadow-purple-200"
                >
                  <span className="text-[14.5px]">Request Correction</span>
                  <ArrowRight size={18} strokeWidth={2.5} />
                </button>
                <div className="attendance-correction-list">
                  {recentCorrections.length === 0 ? (
                    <div className="border border-dashed border-gray-200 rounded-xl p-3.5 text-center bg-gray-50/50">
                      <span className="text-[13px] font-semibold text-gray-500">No correction requests yet</span>
                    </div>
                  ) : (
                    recentCorrections.map((request) => (
                      <div key={request._id} className={`attendance-correction-row ${request.status}`}>
                        <div className="attendance-correction-row-top">
                          <span>{formatDate(request.date)}</span>
                          <strong>{request.status}</strong>
                        </div>
                        <p>
                          {formatTime(request.requestedCheckIn)} - {formatTime(request.requestedCheckOut)} • {request.requestedMode || "office"}
                        </p>
                        {request.reason && <p>{request.reason}</p>}
                        {request.reviewedBy?.name && (
                          <p>Admin: {request.reviewedBy.name}</p>
                        )}
                        {request.status === "rejected" && request.rejectionReason && (
                          <p>Comment: {request.rejectionReason}</p>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>

            </aside>

            <section className="attendance-card attendance-history-card">
              <div className="premium-card-header">
                <div>
                  <span className="premium-section-kicker">Recent records</span>
                  <h2>Attendance history</h2>
                </div>
                <span className="attendance-history-count">{historyRows.length} records</span>
              </div>

              <div className="attendance-history-table">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Mode</th>
                      <th>Check in</th>
                      <th>Check out</th>
                      <th>Hours</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyRows.length === 0 ? (
                      <tr>
                        <td colSpan="6">No attendance records yet</td>
                      </tr>
                    ) : (
                      historyMonthGroups.map((group) => {
                        const isExpanded = expandedHistoryMonths.has(group.key);
                        return (
                          <Fragment key={group.key}>
                            <tr className={`attendance-history-month-row ${group.isCurrentMonth ? "current" : ""}`}>
                              <td colSpan="6">
                                <button
                                  type="button"
                                  className="attendance-history-month-button"
                                  onClick={() => toggleHistoryMonth(group.key)}
                                  aria-expanded={isExpanded}
                                >
                                  <span className="attendance-history-month-icon">
                                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                  </span>
                                  <span className="attendance-history-month-title">
                                    {group.label}
                                    {group.isCurrentMonth && <em>Current month</em>}
                                  </span>
                                  <span className="attendance-history-month-count">{group.rows.length} records</span>
                                </button>
                              </td>
                            </tr>
                            {isExpanded && group.rows.map((item) => {
                              const correctionHint = getAttendanceCorrectionHint(item);
                              const rowKey = item._id || `${item.date}-${item.checkIn || ""}`;
                              return (
                                <Fragment key={rowKey}>
                                  <tr
                                    className={[
                                      isAbsentRecord(item) ? "attendance-history-row-absent" : "",
                                      isWeekOffRecord(item) ? "attendance-history-row-week-off" : "",
                                      isHolidayRecord(item) ? "attendance-history-row-holiday" : "",
                                      isApprovedLeaveRecord(item) ? "attendance-history-row-leave" : "",
                                      correctionHint ? "attendance-history-row-warning" : "",
                                    ].filter(Boolean).join(" ")}
                                  >
                                    {isWeekOffRecord(item) ? (
                                      <td data-label="Weekly off" colSpan="6" className="attendance-week-off-cell">
                                        <span className="attendance-week-off-date">{formatDate(item.date)}</span>
                                        <span className="attendance-week-off-indicator">Weekly Off</span>
                                      </td>
                                    ) : (
                                      <>
                                        <td data-label="Date">{formatDate(item.date)}</td>
                                        <td data-label="Mode" className="capitalize">
                                          {isAbsentRecord(item)
                                            ? "-"
                                            : item.isHolidayPlaceholder
                                              ? item.holidayName || "Holiday"
                                              : item.isApprovedLeavePlaceholder
                                                ? item.leaveName || item.mode || "-"
                                                : item.mode || "-"}
                                        </td>
                                        <td data-label="Check in">{formatHistoryTime(item, item.checkIn)}</td>
                                        <td data-label="Check out">{formatHistoryCheckOut(item)}</td>
                                        <td data-label="Hours">{formatHistoryHours(item)}</td>
                                        <td data-label="Status">
                                          <span className={`attendance-row-status ${String(item.status || "present").toLowerCase()}`}>
                                            {formatHistoryStatus(item.status)}
                                          </span>
                                        </td>
                                      </>
                                    )}
                                  </tr>
                                  {correctionHint && (
                                    <tr key={`${rowKey}-reason`} className="attendance-history-reason-row">
                                      <td colSpan="6">
                                        <div className="attendance-history-reason">
                                          <AlertCircle size={15} />
                                          <div>
                                            <strong>{correctionHint.title}</strong>
                                            {correctionHint.message && <span>{correctionHint.message}</span>}
                                          </div>
                                          {correctionHint.correctionAllowed && (
                                            <button
                                              type="button"
                                              onClick={() => openCorrectionForRecord(item, correctionHint.reason)}
                                            >
                                              Request correction
                                            </button>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                  {item.breaks?.some(b => b.startProof?.photoData || b.endProof?.photoData) && (
                                    <tr key={`${rowKey}-break-proof`}>
                                      <td colSpan="6" className="pt-0 pb-3 px-4">
                                        <div className="flex flex-wrap gap-3 mt-1">
                                          {item.breaks.map((b, bi) => (
                                            <Fragment key={bi}>
                                              {b.startProof?.photoData && (
                                                <div className="flex flex-col items-center gap-1">
                                                  <img
                                                    src={b.startProof.photoData}
                                                    alt={`Lunch in selfie`}
                                                    className="w-14 h-14 rounded-xl object-cover border border-violet-200 shadow-sm"
                                                  />
                                                  <span className="text-[10px] font-semibold text-violet-700">Lunch in selfie</span>
                                                </div>
                                              )}
                                              {b.endProof?.photoData && (
                                                <div className="flex flex-col items-center gap-1">
                                                  <img
                                                    src={b.endProof.photoData}
                                                    alt={`Lunch out selfie`}
                                                    className="w-14 h-14 rounded-xl object-cover border border-blue-200 shadow-sm"
                                                  />
                                                  <span className="text-[10px] font-semibold text-blue-700">Lunch out selfie</span>
                                                </div>
                                              )}
                                            </Fragment>
                                          ))}
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </Fragment>
                              );
                            })}
                          </Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </section>
      </main>

      {selfieAction && (
        <SelfieAttendanceModal
          action={selfieAction === "register" ? "checkin" : selfieAction}
          registered={faceRegistered}
          onRegistered={() => setFaceRegistered(true)}
          onVerified={completeSelfieAttendance}
          onClose={() => setSelfieAction("")}
        />
      )}

      <QRAttendanceModal
        isOpen={qrModal.isOpen}
        actionLabel={qrModal.action}
        onClose={() => setQrModal({ isOpen: false, action: null, coords: null })}
        onScanSuccess={handleQRScanSuccess}
      />

      {earlyCheckoutOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={submitEarlyCheckout} className="w-full max-w-lg bg-[var(--card-bg)] border border-[var(--border-color)] rounded-2xl shadow-[var(--shadow-soft)] p-5 space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <span className="premium-section-kicker">Early checkout</span>
                <h2 className="text-xl font-bold">Reason required before 6:27 PM</h2>
                <p className="mt-1 text-sm text-[var(--text-secondary)]">After checkout, this reason will also unlock today's EOD report.</p>
              </div>
              <button type="button" onClick={() => { setEarlyCheckoutOpen(false); setPendingSelfieCheckoutToken(""); setLocationError(""); }} className="w-9 h-9 rounded-xl border border-[var(--border-color)] flex items-center justify-center"><X size={16} /></button>
            </div>
            
            {locationError && (
              <div className="text-xs font-semibold text-red-600 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/30 rounded-xl px-3 py-2">
                {locationError}
              </div>
            )}

            <label className="space-y-1 block">
              <span className="text-xs font-bold text-[var(--text-secondary)]">Early checkout reason</span>
              <textarea value={earlyCheckoutReason} onChange={(event) => setEarlyCheckoutReason(event.target.value)} className="w-full min-h-28 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-xl px-3 py-3 outline-none resize-none" placeholder={`Explain the reason in at least ${MIN_TEXT_WORDS} words`} autoFocus required />
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setEarlyCheckoutOpen(false); setPendingSelfieCheckoutToken(""); setLocationError(""); }} className="px-4 py-2 rounded-xl border border-[var(--border-color)] font-bold">Cancel</button>
              <button type="submit" disabled={earlyCheckoutSaving} className="px-4 py-2 rounded-xl bg-[var(--accent-primary)] text-white font-bold disabled:opacity-60">{earlyCheckoutSaving ? "Checking out..." : "Submit reason & check out"}</button>
            </div>
          </form>
        </div>
      )}
      {correctionOpen && (
          <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px] flex items-center justify-center p-4">
            <form
              onSubmit={submitCorrection}
              className="w-full max-w-[460px] bg-white rounded-[20px] shadow-[0_8px_30px_rgba(0,0,0,0.12)] p-6 flex flex-col gap-5"
            >
              <div className="flex items-start justify-between">
                <div>
                  <span className="text-[10px] font-bold text-[#8A2BE2] tracking-[0.1em] uppercase block mb-1">Attendance correction</span>
                  <h2 className="text-[22px] font-bold text-[#0F172A] tracking-tight leading-none">Request admin approval</h2>
                </div>
                <button
                  type="button"
                  onClick={() => { setCorrectionOpen(false); setLocationError(""); }}
                  className="text-gray-500 hover:text-gray-900 transition-colors p-1"
                >
                  <X size={20} strokeWidth={2} />
                </button>
              </div>

              {locationError && (
                <div className="text-[13px] font-semibold text-red-600 bg-red-50 border border-red-200 rounded-xl px-3.5 py-2.5">
                  {locationError}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <label className="space-y-1.5">
                  <span className="text-[13px] font-bold text-gray-500">Date</span>
                  <input
                    type="date"
                    value={correctionForm.date}
                    onChange={(event) => setCorrectionForm((current) => ({ ...current, date: event.target.value }))}
                    className="w-full bg-white border border-gray-200 rounded-[14px] px-3.5 py-2.5 text-[15px] font-medium text-gray-900 outline-none focus:border-[#8A2BE2] focus:ring-1 focus:ring-[#8A2BE2] transition-colors"
                    required
                  />
                </label>
                <label className="space-y-1.5">
                  <span className="text-[13px] font-bold text-gray-500">Mode</span>
                  <select
                    value={correctionForm.requestedMode}
                    onChange={(event) => setCorrectionForm((current) => ({ ...current, requestedMode: event.target.value }))}
                    className="w-full bg-white border border-gray-200 rounded-[14px] px-3.5 py-2.5 text-[15px] font-medium text-gray-900 outline-none focus:border-[#8A2BE2] focus:ring-1 focus:ring-[#8A2BE2] transition-colors"
                  >
                    <option value="office">Office</option>
                    <option value="wfh">WFH</option>
                  </select>
                </label>
                <TwelveHourTimeInput
                  label="Check in"
                  value={correctionForm.checkIn}
                  onChange={(value) => setCorrectionForm((current) => ({ ...current, checkIn: value }))}
                />
                <TwelveHourTimeInput
                  label="Check out"
                  value={correctionForm.checkOut}
                  onChange={(value) => setCorrectionForm((current) => ({ ...current, checkOut: value }))}
                />
              </div>

              <label className="space-y-1.5 block">
                <span className="text-[13px] font-bold text-gray-500">Reason</span>
                <textarea
                  value={correctionForm.reason}
                  onChange={(event) => setCorrectionForm((current) => ({ ...current, reason: event.target.value }))}
                  className="w-full min-h-[100px] bg-white border border-gray-200 rounded-[14px] px-3.5 py-3 text-[15px] font-medium text-gray-900 outline-none focus:border-[#8A2BE2] focus:ring-1 focus:ring-[#8A2BE2] transition-colors resize-none placeholder:text-gray-400"
                  placeholder={`Explain what needs to be corrected in at least ${MIN_TEXT_WORDS} words`}
                  required
                />
              </label>

              <div className="flex justify-end gap-3 mt-1">
                <button
                  type="button"
                  onClick={() => { setCorrectionOpen(false); setLocationError(""); }}
                  className="px-4 py-2.5 text-[15px] font-bold text-[#0F172A] hover:bg-gray-50 rounded-[12px] transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={correctionSaving}
                  className="px-6 py-2.5 rounded-[12px] bg-[#8A2BE2] hover:bg-purple-700 text-white text-[15px] font-bold shadow-sm shadow-purple-200 disabled:opacity-60 transition-colors"
                >
                  {correctionSaving ? "Submitting..." : "Submit Request"}
                </button>
              </div>
            </form>
          </div>
      )}
    </div>
  );
};

const getAttendanceView = (attendanceStatus, record, formatTime, formatCheckout) => {
  if (attendanceStatus?.status === "checked_out") {
    return {
      label: "Completed",
      title: "Day complete",
      subtitle: (
        <span className="inline-flex flex-wrap items-center gap-1">
          <span>{formatTime(record?.checkIn)}</span>
          <span>-</span>
          {formatCheckout(record)}
        </span>
      ),
      tone: "success",
      icon: <CheckCircle2 size={28} />,
    };
  }

  if (attendanceStatus?.status === "checked_in") {
    return {
      label: "Live",
      title: "Checked in",
      subtitle: `Started at ${formatTime(record?.checkIn)}`,
      tone: "success",
      icon: <Clock3 size={28} />,
    };
  }

  if (attendanceStatus?.status === "on_break") {
    return {
      label: "On break",
      title: "Break running",
      subtitle: `Started at ${formatTime(record?.breakStart)}`,
      tone: "warning",
      icon: <TimerReset size={28} />,
    };
  }

  return {
    label: "Not checked in",
    title: "You're not checked in yet",
    subtitle: "Start your day by checking in",
    tone: "warning",
    icon: <UserRound size={28} />,
  };
};

const TwelveHourTimeInput = ({ label, value, onChange }) => {
  const parts = toTwelveHourParts(value);
  const updatePart = (key, nextValue) => {
    onChange(fromTwelveHourParts({ ...parts, [key]: nextValue }));
  };

  return (
    <label className="space-y-1.5">
      <span className="text-[13px] font-bold text-gray-500">{label}</span>
      <div className="flex items-center w-full bg-white border border-gray-200 rounded-[14px] overflow-hidden focus-within:border-[#8A2BE2] focus-within:ring-1 focus-within:ring-[#8A2BE2] transition-colors h-[42px]">
        <select
          value={parts.hour}
          onChange={(event) => updatePart("hour", event.target.value)}
          aria-label={`${label} hour`}
          className="flex-1 min-w-0 h-full bg-transparent border-0 text-[15px] font-bold text-gray-900 outline-none px-2 text-center cursor-pointer appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23111827%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[length:10px_10px] bg-[right_8px_center] pr-6"
        >
          <option value="">H</option>
          {TIME_12_HOURS.map((hour) => (
            <option key={hour} value={hour}>{hour}</option>
          ))}
        </select>
        <span className="text-gray-400 font-bold">:</span>
        <select
          value={parts.minute}
          onChange={(event) => updatePart("minute", event.target.value)}
          aria-label={`${label} minute`}
          className="flex-1 min-w-0 h-full bg-transparent border-0 text-[15px] font-bold text-gray-900 outline-none px-2 text-center cursor-pointer appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23111827%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[length:10px_10px] bg-[right_8px_center] pr-6"
        >
          <option value="">M</option>
          {TIME_MINUTES.map((minute) => (
            <option key={minute} value={minute}>{minute}</option>
          ))}
        </select>
        <div className="w-[1px] h-6 bg-gray-200 mx-1"></div>
        <select
          value={parts.period}
          onChange={(event) => updatePart("period", event.target.value)}
          aria-label={`${label} AM or PM`}
          className="w-[68px] h-full bg-transparent border-0 text-[15px] font-bold text-gray-900 outline-none pl-3 pr-6 cursor-pointer appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23111827%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[length:10px_10px] bg-[right_10px_center]"
        >
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
    </label>
  );
};

const TimelineItem = ({ label, value, icon }) => (
  <div className="premium-timeline-item">
    <span className="attendance-timeline-icon">{icon}</span>
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  </div>
);

const AttendanceActionCircle = ({ label, icon, tone = "", onClick }) => (
  <button
    type="button"
    className={`attendance-start-timer ${tone}`}
    onClick={onClick}
  >
    {icon}
    <span>{label}</span>
  </button>
);

export default Attendance;
